import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';

import { ACTIONS } from './actions';
import { mapStartPos, mapToPos } from './hooks/useInEditorDirectStreaming';
import {
	inEditorDirectStreamingPluginKey,
	type InEditorDirectStreamingPluginState,
} from './in-editor-direct-streaming-plugin-key';

const initialState: InEditorDirectStreamingPluginState = {
	showHighlight: false,
	showTelepointer: true,
	isStreaming: false,
	startPos: undefined,
	loadingPos: undefined,
	userContentEndPos: undefined,
	aiContentEndPos: undefined,
	telepointerPos: undefined,
	isFinalStream: false,
	chunkNumber: 0,
};
export const createInEditorDirectStreamingPlugin = (options: {
	getIntl: PMPluginFactoryParams['getIntl'];
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}): SafePlugin => {
	// Create the telepointer element
	const telepointerElement = document.createElement('span');
	telepointerElement.setAttribute('data-testid', `ai-in-editor-telepointer-widget`);
	telepointerElement.className = `ai-in-editor-telepointer`;

	const telepointerLabel = document.createElement('span');
	telepointerLabel.setAttribute('data-testid', `ai-in-editor-telepointer-widget-label`);
	telepointerLabel.className = `ai-in-editor-telepointer-label`;
	telepointerLabel.append(options.getIntl().formatMessage(aiMessages.aiRovoTelepointer));
	telepointerElement.appendChild(telepointerLabel);

	return new SafePlugin<InEditorDirectStreamingPluginState>({
		key: inEditorDirectStreamingPluginKey,
		state: {
			init: () => {
				return initialState;
			},
			apply: (tr, pluginState) => {
				const meta = tr.getMeta(inEditorDirectStreamingPluginKey);
				let newPluginState: InEditorDirectStreamingPluginState = pluginState;
				if (meta?.action) {
					switch (meta.action) {
						case ACTIONS.INIT_POSITIONS: {
							const { positions, showHighlight, showTelepointer } = meta;
							const { startPos, userContentEndPos } = positions;
							newPluginState = {
								...newPluginState,
								showHighlight,
								showTelepointer,
								isStreaming: true,
								startPos,
								loadingPos: startPos,
								userContentEndPos,
								aiContentEndPos: undefined,
							};
							break;
						}
						case ACTIONS.ADJUST_INIT_POSITIONS: {
							const { positions } = meta;
							const { startPos, userContentEndPos } = positions;
							newPluginState = {
								...newPluginState,
								startPos,
								userContentEndPos,
							};
							break;
						}
						case ACTIONS.UPDATE_LOADING_POSITIONS: {
							const { startPos, userContentEndPos, loadingPos } = pluginState;

							if (startPos === undefined || userContentEndPos === undefined) {
								break;
							}

							const max = userContentEndPos;
							const min = loadingPos || startPos;

							const eighth = (max - min) / 8;
							const low = Math.floor(min + eighth);
							const high = Math.ceil(min + 3 * eighth);
							const newLoadingPos = Math.ceil(Math.random() * (high - low) + low);

							newPluginState = {
								...newPluginState,
								loadingPos: tr.mapping.map(newLoadingPos, -1),
							};
							break;
						}
						case ACTIONS.UPDATE_STREAM_POSITIONS: {
							const { positions, isFinalStream } = meta;
							const { aiContentEndPos, telepointerPos } = positions;
							newPluginState = {
								...newPluginState,
								loadingPos: undefined,
								aiContentEndPos,
								telepointerPos,
								isFinalStream,
								chunkNumber: newPluginState.chunkNumber + 1,
							};
							break;
						}
						case ACTIONS.END_STREAM: {
							return initialState;
						}
					}
				} else {
					// These positions will be mapped by the plugin's transaction
					// So we map them only when the transaction is not related to the plugin
					newPluginState = {
						...newPluginState,
						...(newPluginState.loadingPos !== undefined && {
							loadingPos: tr.mapping.map(newPluginState.loadingPos, -1),
						}),
						...(newPluginState.telepointerPos !== undefined && {
							telepointerPos: tr.mapping.map(newPluginState.telepointerPos, -1),
						}),
					};
				}

				newPluginState = {
					...newPluginState,
					...(newPluginState.startPos !== undefined && {
						startPos: mapStartPos({ pos: newPluginState.startPos, tr }),
					}),
					...(newPluginState.userContentEndPos !== undefined && {
						userContentEndPos: mapToPos({ pos: newPluginState.userContentEndPos, tr }),
					}),
					...(newPluginState.aiContentEndPos !== undefined && {
						aiContentEndPos: mapToPos({ pos: newPluginState.aiContentEndPos, tr }),
					}),
				};
				return newPluginState;
			},
		},
		props: {
			decorations: (state) => {
				const pluginState = inEditorDirectStreamingPluginKey.getState(state);
				const {
					showHighlight,
					showTelepointer,
					startPos,
					userContentEndPos,
					aiContentEndPos,
					telepointerPos,
					loadingPos,
					isFinalStream,
					isStreaming,
				} = pluginState || {};
				const decorations = [];
				if (!isStreaming || startPos === undefined) {
					return DecorationSet.empty;
				}

				if (showHighlight) {
					if (loadingPos !== undefined) {
						decorations.push(
							Decoration.inline(startPos, loadingPos, {
								class: 'ai-in-editor-user-content',
								'data-testid': 'ai-in-editor-user-content',
							}),
						);
					} else {
						if (userContentEndPos !== undefined) {
							decorations.push(
								Decoration.inline(aiContentEndPos || startPos, userContentEndPos, {
									class: 'ai-in-editor-user-content',
									'data-testid': 'ai-in-editor-user-content',
								}),
							);
						}
					}

					if (aiContentEndPos !== undefined) {
						decorations.push(
							Decoration.inline(startPos, aiContentEndPos, {
								class: 'ai-in-editor-ai-content',
								'data-testid': 'ai-in-editor-ai-content',
							}),
						);
					}
				}

				/**
				 * We show the telepointer only when isFinalStream is false.
				 * On the final stream, plugin state is reset so isFinalStream will be undefined.
				 */
				if (isFinalStream === false && showTelepointer) {
					const telepointer = Decoration.widget(
						telepointerPos || aiContentEndPos || startPos,
						telepointerElement,
						{
							side: 0,
							key: 'inEditorAIContentDecoration',
						},
					);

					decorations.push(telepointer);
				}

				if (decorations.length) {
					return DecorationSet.create(state.doc, decorations);
				}
				return DecorationSet.empty;
			},
		},
		view() {
			let hasReset = false;
			return {
				update(view) {
					const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
					const { isStreaming } = pluginState || {};
					// At the start of streaming reset
					if (isStreaming && !hasReset) {
						hasReset = true;
						options.api?.core?.actions.execute(options.api?.trackChanges?.commands.resetBaseline);
					}
					// When streaming concludes allow for
					if (!isStreaming) {
						hasReset = false;
					}
				},
			};
		},
	});
};
